import getSymbolFromCurrency from 'currency-symbol-map';
import Link from 'next/link';
import { FC, ReactNode } from 'react';

import EventTicketsStatus from './EventTicketsStatus';

import PrimaryButton from '@/components/Module/PrimaryButton';
import SecondaryButton from '@/components/Module/SecondaryButton';
import useViewport from '@/hooks/useViewport';
import { Price } from '@/types/price';

export type EventBuyTicketsContainerProps = {
  id: string;
  priceRange: [Price] | [Price, Price] | [];
  status: EventTicketsStatus;
  venueWebsiteUrl: string | null;
  venueName: string;
};

type BuyTicketsButtonProps = {
  id: string;
  status: EventTicketsStatus;
};

const formatPrice = (price: Price) => {
  if (price.amount.equals(0)) {
    return `Free`;
  }

  return `${getSymbolFromCurrency(price.currency)}${price.amount.toFixed(2)}`;
};

const ForwardArrow: FC<{
  children?: ReactNode;
}> = (props) => {
  const {} = props;

  return (
    <svg
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M17.92 11.6202C17.8724 11.4974 17.801 11.3853 17.71 11.2902L12.71 6.29019C12.6168 6.19695 12.5061 6.12299 12.3842 6.07253C12.2624 6.02207 12.1319 5.99609 12 5.99609C11.7337 5.99609 11.4783 6.10188 11.29 6.29019C11.1968 6.38342 11.1228 6.49411 11.0723 6.61594C11.0219 6.73776 10.9959 6.86833 10.9959 7.00019C10.9959 7.26649 11.1017 7.52188 11.29 7.71019L14.59 11.0002H7C6.73478 11.0002 6.48043 11.1055 6.29289 11.2931C6.10536 11.4806 6 11.735 6 12.0002C6 12.2654 6.10536 12.5198 6.29289 12.7073C6.48043 12.8948 6.73478 13.0002 7 13.0002H14.59L11.29 16.2902C11.1963 16.3831 11.1219 16.4937 11.0711 16.6156C11.0203 16.7375 10.9942 16.8682 10.9942 17.0002C10.9942 17.1322 11.0203 17.2629 11.0711 17.3848C11.1219 17.5066 11.1963 17.6172 11.29 17.7102C11.383 17.8039 11.4936 17.8783 11.6154 17.9291C11.7373 17.9798 11.868 18.006 12 18.006C12.132 18.006 12.2627 17.9798 12.3846 17.9291C12.5064 17.8783 12.617 17.8039 12.71 17.7102L17.71 12.7102C17.801 12.6151 17.8724 12.5029 17.92 12.3802C18.02 12.1367 18.02 11.8636 17.92 11.6202Z"
        fill="white"
      />
    </svg>
  );
};
const MobileBuyTicketsButton = (props: BuyTicketsButtonProps) => {
  const { status, id } = props;

  switch (status) {
    case `available`: {
      return (
        <Link href={`/events/${id}/checkout`} replace>
          <a>
            <PrimaryButton py={17}>Get Tickets</PrimaryButton>
          </a>
        </Link>
      );
    }
    case `cancelled`: {
      return (
        <PrimaryButton disabled={true} py={17}>
          Cancelled
        </PrimaryButton>
      );
    }
    case `unavailable`: {
      return (
        <PrimaryButton disabled={true} py={17}>
          Unavailable at the moment
        </PrimaryButton>
      );
    }
    case `ended`: {
      return (
        <PrimaryButton disabled={true} py={17}>
          This event has ended
        </PrimaryButton>
      );
    }
    case `off-sale`: {
      return (
        <div className={`flex flex-col items-center gap-[12px]`}>
          <PrimaryButton disabled={true} py={17}>
            Event Off Sale
          </PrimaryButton>
          <p className={`text-b3 font-normal text-gray-300`}>
            Tickets may still be available at the door
          </p>
        </div>
      );
    }
  }

  return null;
};

const MobileEventBuyTicketsContainer = (
  props: EventBuyTicketsContainerProps,
) => {
  const { id, priceRange, status, venueWebsiteUrl, venueName } = props;

  if (status === `no-tickets`) {
    return (
      <div
        className={`flex justify-between gap-[24px] bg-gray-0 px-[24px] py-[20px]`}
        style={{
          boxShadow: `0px -8px 30px rgba(153, 153, 153, 0.24)`,
        }}
      >
        <div className={`flex w-[255px] items-center justify-center`}>
          <p
            className={`max-w-full break-words font-roboto text-b2 font-normal tracking-tight text-gray-300`}
          >
            Make a Reservation at {venueName}
          </p>
        </div>
        {venueWebsiteUrl && (
          <div className={`flex items-center justify-center`}>
            <div className={`h-[48px] w-[48px]`}>
              <Link href={venueWebsiteUrl} replace>
                <a className={`contents`}>
                  <SecondaryButton py={11}>
                    <div className="flex items-center justify-center">
                      <ForwardArrow />
                    </div>
                  </SecondaryButton>
                </a>
              </Link>
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div
      className={`flex justify-between gap-[20px] bg-gray-0 px-[24px] py-[20px]`}
      style={{
        boxShadow: `0px -8px 30px rgba(153, 153, 153, 0.24)`,
      }}
    >
      {status === `available` && priceRange.length == 2 && (
        <div className={`flex w-full items-center justify-center`}>
          <p className={`text-h4 font-medium text-gray-400`}>
            {formatPrice(priceRange[0])} - {formatPrice(priceRange[1])}
          </p>
        </div>
      )}
      {status === `available` && priceRange.length == 1 && (
        <div className={`flex w-full items-center justify-start`}>
          <p className={`text-h4 font-medium text-gray-400`}>
            {formatPrice(priceRange[0])}
          </p>
        </div>
      )}

      <div className={`flex w-full items-center justify-center`}>
        <div className={`w-full`}>
          <MobileBuyTicketsButton id={id} status={status} />
        </div>
      </div>
    </div>
  );
};

const DesktopEventBuyTicketsContainer = (
  props: EventBuyTicketsContainerProps,
) => {
  const { id, priceRange, status, venueWebsiteUrl, venueName } = props;

  switch (status) {
    case `cancelled`: {
      return (
        <div className={`w-[327px]`}>
          <PrimaryButton disabled={true} py={17}>
            Cancelled
          </PrimaryButton>
        </div>
      );
    }
    case `unavailable`: {
      return (
        <div className={`w-[327px]`}>
          <PrimaryButton disabled={true} py={17}>
            Unavailable at the moment
          </PrimaryButton>
        </div>
      );
    }
    case `ended`: {
      return (
        <div className={`w-[327px]`}>
          <PrimaryButton disabled={true} py={17}>
            This event has ended
          </PrimaryButton>
        </div>
      );
    }
    case `off-sale`: {
      return (
        <div className={`flex w-[327px] flex-col items-start gap-[16px]`}>
          <PrimaryButton disabled={true} py={17}>
            Event Off Sale
          </PrimaryButton>
          <p className={`text-b3 font-normal text-gray-300`}>
            Tickets may still be available at the door
          </p>
        </div>
      );
    }
    case `no-tickets`: {
      return (
        <div
          className={`flex max-w-full justify-between gap-[20px] rounded-[14px] bg-gray-0 px-[24px] py-[20px]`}
          style={{
            boxShadow: `0px -8px 30px rgba(153, 153, 153, 0.24)`,
          }}
        >
          <div className={`flex w-[252px] items-center justify-center`}>
            <p
              className={`max-w-full break-words font-roboto text-b2 font-normal tracking-tight text-gray-400`}
            >
              Make a Reservation at {venueName}
            </p>
          </div>
          {venueWebsiteUrl && (
            <div className={`flex items-center justify-center`}>
              <div className={`w-[160px]`}>
                <Link href={venueWebsiteUrl} replace>
                  <a className={`contents`}>
                    <SecondaryButton py={17}>
                      <div className="flex w-full grow items-center justify-center gap-[10px]">
                        Book Now
                        <ForwardArrow />
                      </div>
                    </SecondaryButton>
                  </a>
                </Link>
              </div>
            </div>
          )}
        </div>
      );
    }
  }

  return (
    <div
      className={`flex max-w-full justify-between gap-[80px] rounded-[14px] bg-gray-0 px-[24px] py-[20px]`}
      style={{
        boxShadow: `0px -8px 30px rgba(153, 153, 153, 0.24)`,
      }}
    >
      {status === `available` && priceRange.length == 2 && (
        <div className={`flex max-w-[50%] items-center justify-center`}>
          <p
            className={`max-w-full break-words text-h4 font-medium text-gray-400`}
          >
            {formatPrice(priceRange[0])} - {formatPrice(priceRange[1])}
          </p>
        </div>
      )}
      {status === `available` && priceRange.length == 1 && (
        <div className={`flex max-w-[50%] items-center justify-start`}>
          <p
            className={`max-w-full break-words text-h4 font-medium text-gray-400`}
          >
            {formatPrice(priceRange[0])}
          </p>
        </div>
      )}
      <div className={`flex items-center justify-center`}>
        <div className={`w-[160px]`}>
          <Link href={`/events/${id}/checkout`} replace>
            <a>
              <PrimaryButton py={17}>Get Tickets</PrimaryButton>
            </a>
          </Link>
        </div>
      </div>
    </div>
  );
};

const EventBuyTicketsContainer = (props: EventBuyTicketsContainerProps) => {
  const {} = props;
  const viewport = useViewport();

  if (viewport == `mobile` || viewport == `tablet`) {
    return <MobileEventBuyTicketsContainer {...props} />;
  }
  if (viewport == `desktop`) {
    return <DesktopEventBuyTicketsContainer {...props} />;
  }

  return null;
};

export default EventBuyTicketsContainer;
